/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Auth } from '@aws-amplify/auth';
import { Hub } from 'aws-amplify';
import { ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { Role, SnackbarSeverity, UserType } from './Shared/Constants/App';
import MainLayout from './Shared/Components/Layout/MainLayout';
import LoginPage from './Modules/Onboarding/Components/Login/LoginPage';
import PageNotFound from './Modules/Onboarding/Components/PageNotFound/PageNotFound';
import { routes } from './routes';
import theme from './Shared/Themes/theme';
import './index.css';
import AuthProvider from './Configuration/AuthContext';
import MasterDataApiService from './Shared/Services/MasterDataService';
import SnackbarMessage from './Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import BidApprovalEmail from './Modules/Customer/Components/BidApprovalEmail/BidApprovalEmail';
import { FeatureSwitchProvider } from './Infrastructure/FeatureSwitch/FeatureSwitchProvider';
import { FeatureKeys } from './Infrastructure/FeatureSwitch';
import { AuthenticationService } from './Shared/Services/AuthenticationService';
import { clearLoginToken } from './Shared/Utilities/utils';
import OAuthCallback from './Modules/Onboarding/Components/OAuthCallback/OAuthCallback';
import OpenDeepLink from './Modules/Onboarding/Components/OpenDeepLink/OpenDeepLink';

export interface IRole {
  roleId: string;
  roleName: string;
}

// Defined once, outside App: a new array on every App render made FeatureSwitchProvider remount the app.
const ENABLED_BY_DEFAULT_FEATURES = [FeatureKeys.AccessPreShiftCheckList];

const App = (): JSX.Element => {
  const { t } = useTranslation(['signIn']);
  const [snackbarDetails, setSnackbarDetails] = useState({
    isOpen: false,
    errorMsg: 'Your session has expired. Please log in again',
    successMsg: '',
    severity: 'error',
  });

  const signOut = useCallback(async () => {
    localStorage.removeItem('auth');
    localStorage.removeItem('customerDetails');
    setSnackbarDetails((prevState) => {
      return {
        ...prevState,
        isOpen: true,
        severity: 'error',
      };
    });
    await Auth.signOut();
  }, []);

  const completeSignIn = async (): Promise<boolean> => {
    const { customer, loggedInUser, userId } = await AuthenticationService.trackLoginSetup(
      (async () => {
        // One call for the user and their organization (userExist v2).
        const [{ customer, userId }, loggedInUser] = await Promise.all([
          AuthenticationService.getSignedInUserDetails(),
          MasterDataApiService.getCurrentUserInfo(),
        ]);
        await AuthenticationService.sendSignInAuthEvent(userId, customer?.data?.organization?.organizationId);
        return { customer, loggedInUser, userId };
      })()
    );
    // Processes only need the org details, so they load alongside user-scopes instead of after it.
    const processesRequest = customer?.data
      ? AuthenticationService.fetchAllProcesses(customer.data.userId, customer.data.organization.organizationId)
      : null;
    const { userScopesResponse, customersList } = await MasterDataApiService.getLoginRolesAndCustomers(
      userId,
      customer?.data?.organization?.organizationId
    );
    if (customersList) localStorage.setItem('customersList', JSON.stringify(customersList));
    let userRoles: string[] = [];
    if (userScopesResponse.data) {
      userRoles = userScopesResponse.data
        .map((scope: any) => {
          return scope?.roles?.map((role: IRole) => {
            return role.roleName;
          });
        })
        .flat();
      loggedInUser.canCreateOneTimeJob = userScopesResponse.data[0]?.canCreateOneTimeJob ?? false;
    }
    const userType = loggedInUser.attributes['custom:Role'];
    if (userType === UserType.Customer) userRoles = [Role.Customer];
    loggedInUser.roles = userRoles;

    if (userType === UserType.Customer || userType === UserType.Vendor) {
      localStorage.setItem('auth', JSON.stringify({ ...loggedInUser, signedInTime: new Date().getTime() }));
      if (customer.data) {
        localStorage.setItem('customerDetails', JSON.stringify(customer.data));
        const processes = await processesRequest;
        if (processes?.data) {
          localStorage.setItem('processData', JSON.stringify(processes?.data));
        }
      }
      sessionStorage.setItem('showLoginSuccess', 'true');
      window.location.pathname = '/home';
      return true;
    }
    return false;
  };

  const refreshSession = async (): Promise<void> => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      if (currentUser) {
        await Auth.currentSession();
      }
    } catch (e) {
      console.log(e);
    }
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    return Hub.listen('auth', async ({ payload: { event } }) => {
      switch (event) {
        case 'implicitFlow':
          break;
        case 'signOut':
          localStorage.removeItem('auth');
          localStorage.removeItem('customerDetails');
          localStorage.removeItem('userDetails');
          localStorage.removeItem('screenName');
          localStorage.removeItem('__permissionUser');
          clearLoginToken();
          AuthenticationService.resetSignInAuthEvent();

          break;
        case 'signIn':
          // The login forms wait on this before showing the home page, so it only loads once the details it
          // reads (customerDetails, customersList, roles, processData) are stored.
          await AuthenticationService.trackLoginCompletion(completeSignIn());
          break;
        case 'cognitoHostedUI_failure':
          window.location.href = '/login?error=sso_failed';
          break;
        case 'signIn_failure':
          //wrong password or OTP; the login form shows that error itself, so only an SSO sign-in
          // (back on /oauth) is sent to the login page.
          if (window.location.pathname === '/oauth') window.location.href = '/login?error=sso_processing_failed';
          break;
        case 'tokenRefresh_failure':
          signOut();
          break;
        default:
      }
    });
  }, []);

  useEffect(() => {
    refreshSession();
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('showLoginSuccess')) {
      sessionStorage.removeItem('showLoginSuccess');
      setSnackbarDetails((prevState) => ({
        ...prevState,
        isOpen: true,
        successMsg: t('signIn:loginSuccessful'),
        severity: SnackbarSeverity.success,
      }));
    }
  }, []);

  const { isOpen, errorMsg, successMsg, severity } = snackbarDetails;

  return (
    <ThemeProvider theme={theme}>
      <FeatureSwitchProvider enabledByDefault={ENABLED_BY_DEFAULT_FEATURES}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/" element={<MainLayout />}>
                  {routes}
                </Route>
                <Route path="/email-transactions" element={<BidApprovalEmail />} />
                <Route path="/oauth" element={<OAuthCallback />} />
                <Route path="/open/*" element={<OpenDeepLink />} />
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
          <SnackbarMessage
            open={isOpen}
            successMessage={successMsg}
            errorMessage={errorMsg}
            severity={severity}
            onClose={() =>
              setSnackbarDetails((prevState) => {
                return {
                  ...prevState,
                  isOpen: false,
                };
              })
            }
          />
        </LocalizationProvider>
      </FeatureSwitchProvider>
    </ThemeProvider>
  );
};

export default App;
